import React, { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';
import SupportRequestModal from '../help/SupportRequestModal';
import { useToast } from '../ui/Toast';
import { useTranslation } from '../../translations';
import { bladPremium, zgloszenieChcePremium } from '../../utils/premium';

// „Chcę Premium” (F.2): JEDNO okno zgłoszenia na cały panel, to samo co z przycisku pomocy (H.1,
// POST /api/support-request), otwarte z kategorią pytania, tematem i nazwą funkcji w treści.
// Bez nowej trasy: zgłoszenie trafia do nas jak każde inne. Dostawca stoi w App.js nad menu
// i stronami, więc „Chcę Premium” działa z plakietki w menu, z informacji na stronie i z komunikatu
// po odmowie serwera.

const PremiumUpgradeContext = createContext(null);

export function PremiumUpgradeProvider({ user, children }) {
  const { t } = useTranslation();
  // null = zamknięte; tekst = slug funkcji (pusty, gdy nie wiadomo której).
  const [funkcja, setFunkcja] = useState(null);
  const wyzwalaczRef = useRef(null);

  const otworz = useCallback((feature) => {
    wyzwalaczRef.current = typeof document !== 'undefined' ? document.activeElement : null;
    setFunkcja(typeof feature === 'string' ? feature : '');
  }, []);

  const zamknij = useCallback(() => {
    setFunkcja(null);
    // Fokus wraca tam, skąd otwarto okno (przycisk na stronie albo w menu).
    const el = wyzwalaczRef.current;
    if (el && typeof el.focus === 'function' && document.contains(el)) el.focus();
  }, []);

  const wstepne = useMemo(
    () => (funkcja === null ? null : zgloszenieChcePremium(t, funkcja)),
    [funkcja, t]
  );

  return (
    <PremiumUpgradeContext.Provider value={otworz}>
      {children}
      <SupportRequestModal
        open={funkcja !== null}
        kategoriaStartowa="question"
        wstepne={wstepne}
        onClose={zamknij}
        user={user}
        // Nad nakładką szczegółów rezerwacji, z której bywa otwierane (informacja przy linkach do zapłaty).
        elevated
      />
    </PremiumUpgradeContext.Provider>
  );
}

/** Funkcja otwierająca „Chcę Premium” (`otworz(feature)`) albo null poza dostawcą. */
export function usePremiumUpgrade() {
  return useContext(PremiumUpgradeContext);
}

/**
 * Wspólna obsługa odpowiedzi 403 `premium_required` z tras z bramką planu: komunikat serwera
 * w powiadomieniu z przyciskiem „Chcę Premium”. Zwraca true, gdy to była odmowa planu (wołający
 * kończy wtedy obsługę błędu), false dla każdej innej odpowiedzi.
 * Przyjmuje ciało odpowiedzi fetch albo wyjątek axios.
 */
export function usePremiumBlad() {
  const toast = useToast();
  const otworz = usePremiumUpgrade();
  const { t } = useTranslation();
  return useCallback((zrodlo) => {
    const odmowa = bladPremium(zrodlo);
    if (!odmowa) return false;
    // Do zamknięcia (duration 0): komunikat niesie przycisk, który musi poczekać na użytkownika.
    toast.error(odmowa.message || t('premium.defaultMessage'), {
      duration: 0,
      ...(otworz ? { action: { label: t('premium.want'), onClick: () => otworz(odmowa.feature) } } : {}),
    });
    return true;
  }, [toast, otworz, t]);
}
